// C10 — Invitation landing page (route /invitation/:token).
//
// Public page. Calls C6 endpoints:
//   GET  /api/invitations/{token}              lookup (public)
//   POST /api/invitations/{token}/accept       auth + strict email match
//   POST /api/invitations/{token}/decline      auth + strict email match
//
// Renders one of seven dedicated state UIs:
//   • PendingNotLoggedIn
//   • PendingEmailMismatch
//   • PendingReadyToAccept
//   • AcceptedState         (from lookup OR after successful accept)
//   • ExpiredState          (410 invite_expired)
//   • DeclinedState         (410 invite_declined OR after successful decline)
//   • RemovedState          (410 invite_removed)
//   • NotFoundState         (404 invite_not_found)
//
// Per the master spec: never auto-redirects. Every terminal state has
// an explicit user action (Open Resource, Sign Out, etc.).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader2, Mail, Lock, CheckCircle2, XCircle, Clock, AlertTriangle,
  ArrowRight, LogOut, FileText, Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AuthModal from '../components/Layout/AuthModal';
import { invitationApi, INVITATION_TERMINAL_COPY } from '../lib/invitationApi';

const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';
// Saved across the Sign In / Create Account round-trip so the user
// returns to this exact invitation after authentication.
const PENDING_INVITE_KEY = 'ivisora_pending_invite_token';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';


// ── Helpers ─────────────────────────────────────────────────────────────
function getLocalUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
}

function formatExpiry(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return null;
  }
}

function signOutAndStayHere(token) {
  // Preserve the pending-invite token so the user can sign in as the
  // correct account and land back here automatically.
  localStorage.setItem(PENDING_INVITE_KEY, token);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Hard reload so every component re-reads localStorage.
  window.location.href = `/invitation/${token}`;
}


// ── Page ────────────────────────────────────────────────────────────────
export default function InvitationPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [view, setView] = useState({ phase: 'loading' });
  const [user, setUser] = useState(() => getLocalUser());
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState(null);

  const isLoggedIn = Boolean(localStorage.getItem(AUTH_TOKEN_KEY));

  // ── Initial lookup ────────────────────────────────────────────────────
  const fetchInvitation = useCallback(async () => {
    if (!token) {
      setView({ phase: 'not_found' });
      return;
    }
    try {
      const data = await invitationApi.lookup(token);
      setView({ phase: data.status, invite: data });
    } catch (err) {
      if (err.status === 410 && err.detail) {
        const codeMap = {
          invite_expired: 'expired',
          invite_declined: 'declined',
          invite_removed: 'removed',
        };
        setView({
          phase: codeMap[err.code] || 'gone',
          terminal: INVITATION_TERMINAL_COPY[err.code] || {
            title: 'Invitation unavailable',
            body: err.message || 'This invitation is no longer valid.',
          },
          gone: err.detail,   // resource_type + resource_id if present
        });
        return;
      }
      if (err.status === 404) {
        setView({
          phase: 'not_found',
          terminal: INVITATION_TERMINAL_COPY.invite_not_found,
        });
        return;
      }
      setView({
        phase: 'error',
        terminal: {
          title: 'Something went wrong',
          body: err.message || 'Could not load this invitation. Try refreshing.',
        },
      });
    }
  }, [token]);

  useEffect(() => { fetchInvitation(); }, [fetchInvitation]);

  // ── Auth modal → success means we just signed in. Re-fetch + clear
  //    the pending-invite localStorage flag so we don't loop. ────────────
  const onAuthSuccess = useCallback(async () => {
    setAuthOpen(false);
    localStorage.removeItem(PENDING_INVITE_KEY);
    // Refresh local user so the page can branch correctly. The Header
    // also calls /api/auth/me; we do it here so we don't depend on
    // its order of execution.
    const token2 = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token2) {
      try {
        const r = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token2}` },
        });
        if (r.ok) {
          const me = await r.json();
          localStorage.setItem(USER_KEY, JSON.stringify(me));
          setUser(me);
        }
      } catch {/* surfaced when we re-fetch the invitation */}
    }
    await fetchInvitation();
  }, [fetchInvitation]);

  // ── Mutations: accept / decline ──────────────────────────────────────
  const handleAccept = useCallback(async () => {
    setActing(true);
    setActionError(null);
    try {
      const result = await invitationApi.accept(token);
      setView((prev) => ({
        phase: 'accepted',
        invite: prev.invite,
        result,
      }));
    } catch (err) {
      // Backend may have transitioned state under us — branch by code
      // so we never leave the user on a stale screen.
      if (err.code === 'invite_already_accepted') {
        setView((prev) => ({
          phase: 'accepted',
          invite: prev.invite,
          result: {
            resource_type: err.detail?.resource_type,
            resource_id: err.detail?.resource_id,
          },
        }));
        return;
      }
      if (err.code === 'email_mismatch') {
        // Surface the right-account UX. Re-fetch so other status flips
        // (revoked while we were on the page) are also caught.
        setActionError(err.message);
        await fetchInvitation();
        return;
      }
      if (err.code === 'invite_expired' || err.code === 'invite_declined' || err.code === 'invite_removed') {
        await fetchInvitation();
        return;
      }
      setActionError(err.message || 'Could not accept this invitation.');
    } finally {
      setActing(false);
    }
  }, [token, fetchInvitation]);

  const handleDecline = useCallback(async () => {
    setActing(true);
    setActionError(null);
    try {
      await invitationApi.decline(token);
      setView({
        phase: 'declined',
        terminal: {
          title: 'Invitation declined',
          body: 'You declined this invitation. If this was a mistake, the owner can send a new one.',
        },
      });
    } catch (err) {
      if (err.code === 'email_mismatch') {
        setActionError(err.message);
        await fetchInvitation();
        return;
      }
      setActionError(err.message || 'Could not decline this invitation.');
    } finally {
      setActing(false);
    }
  }, [token, fetchInvitation]);

  // ── Open resource after accept ───────────────────────────────────────
  const openResource = useCallback((kind, id) => {
    if (!kind || !id) return;
    if (kind === 'design') navigate(`/editor/${id}`);
    else if (kind === 'template') navigate(`/template/${id}`);
  }, [navigate]);


  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50/30 px-4 py-12 flex items-start justify-center">
      <div className="w-full max-w-md" data-testid="invitation-page">
        {view.phase === 'loading' && <LoadingCard />}

        {view.phase === 'pending' && (
          (() => {
            if (!isLoggedIn) {
              return (
                <PendingNotLoggedIn
                  invite={view.invite}
                  onSignIn={() => {
                    localStorage.setItem(PENDING_INVITE_KEY, token);
                    setAuthTab('login');
                    setAuthOpen(true);
                  }}
                  onCreate={() => {
                    localStorage.setItem(PENDING_INVITE_KEY, token);
                    setAuthTab('signup');
                    setAuthOpen(true);
                  }}
                />
              );
            }
            const inviteEmail = (view.invite?.collaborator_email || '').toLowerCase();
            const userEmail = (user?.email || '').toLowerCase();
            if (userEmail && inviteEmail && userEmail !== inviteEmail) {
              return (
                <PendingEmailMismatch
                  invite={view.invite}
                  currentEmail={user.email}
                  onSwitchAccount={() => signOutAndStayHere(token)}
                />
              );
            }
            return (
              <PendingReadyToAccept
                invite={view.invite}
                acting={acting}
                actionError={actionError}
                onAccept={handleAccept}
                onDecline={handleDecline}
              />
            );
          })()
        )}

        {view.phase === 'accepted' && (
          <AcceptedState
            invite={view.invite}
            resourceType={view.result?.resource_type || view.invite?.resource_type}
            resourceId={view.result?.resource_id || view.invite?.resource_id}
            onOpenResource={openResource}
          />
        )}

        {view.phase === 'expired' && (
          <TerminalState
            icon={Clock}
            tone="amber"
            title={view.terminal.title}
            body={view.terminal.body}
            extra={
              view.invite?.expires_at && (
                <p className="text-[11px] text-gray-500 mt-2">
                  Expired on {formatExpiry(view.invite.expires_at)}
                </p>
              )
            }
            navigate={navigate}
          />
        )}
        {view.phase === 'declined' && (
          <TerminalState
            icon={XCircle}
            tone="gray"
            title={view.terminal.title}
            body={view.terminal.body}
            navigate={navigate}
          />
        )}
        {view.phase === 'removed' && (
          <TerminalState
            icon={AlertTriangle}
            tone="amber"
            title={view.terminal.title}
            body={view.terminal.body}
            navigate={navigate}
          />
        )}
        {view.phase === 'not_found' && (
          <TerminalState
            icon={AlertTriangle}
            tone="gray"
            title={view.terminal?.title || INVITATION_TERMINAL_COPY.invite_not_found.title}
            body={view.terminal?.body || INVITATION_TERMINAL_COPY.invite_not_found.body}
            navigate={navigate}
          />
        )}
        {view.phase === 'error' && (
          <TerminalState
            icon={AlertTriangle}
            tone="red"
            title={view.terminal.title}
            body={view.terminal.body}
            extra={
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={fetchInvitation}
                data-testid="retry-btn"
              >
                Try again
              </Button>
            }
            navigate={navigate}
          />
        )}
      </div>

      <AuthModal
        open={authOpen}
        defaultTab={authTab}
        onClose={() => setAuthOpen(false)}
        onAuthSuccess={onAuthSuccess}
      />
    </div>
  );
}


// ── Card sub-components ─────────────────────────────────────────────────
function Card({ children, testId }) {
  return (
    <div
      className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function ResourceCard({ invite }) {
  if (!invite) return null;
  const ResourceIcon = invite.resource_type === 'template' ? Layers : FileText;
  return (
    <div className="mb-6 text-center" data-testid="invite-resource-card">
      <span
        className="inline-flex w-14 h-14 rounded-2xl items-center justify-center
                   bg-indigo-50 text-indigo-600 mb-3"
      >
        <ResourceIcon className="w-7 h-7" />
      </span>
      <p
        className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold"
        data-testid="invite-resource-type"
      >
        {invite.resource_type}
      </p>
      <h1
        className="text-lg font-semibold text-gray-900 mt-0.5 break-words"
        data-testid="invite-resource-name"
      >
        {invite.resource_name || 'Untitled'}
      </h1>
      <p className="text-sm text-gray-500 mt-2" data-testid="invite-owner-name">
        {invite.owner_name || 'A collaborator'} invited you as
        {' '}
        <span className="font-medium text-gray-700 capitalize">{invite.role}</span>
      </p>
      {invite.expires_at && (
        <p className="text-[11px] text-gray-400 mt-2">
          Expires on {formatExpiry(invite.expires_at)}
        </p>
      )}
    </div>
  );
}

function LoadingCard() {
  return (
    <Card testId="invite-loading">
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    </Card>
  );
}

function PendingNotLoggedIn({ invite, onSignIn, onCreate }) {
  return (
    <Card testId="state-not-logged-in">
      <ResourceCard invite={invite} />
      <div className="bg-indigo-50/60 border border-indigo-100 rounded-md px-3 py-2 text-xs text-indigo-700 mb-4">
        <Lock className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
        Sign in as <strong>{invite?.collaborator_email}</strong> to accept this invitation.
      </div>
      <div className="space-y-2">
        <Button
          type="button"
          className="w-full"
          onClick={onSignIn}
          data-testid="signin-btn"
        >
          Sign In
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onCreate}
          data-testid="signup-btn"
        >
          Create Account
        </Button>
      </div>
    </Card>
  );
}

function PendingEmailMismatch({ invite, currentEmail, onSwitchAccount }) {
  return (
    <Card testId="state-email-mismatch">
      <ResourceCard invite={invite} />
      <div className="bg-amber-50 border border-amber-100 rounded-md p-4 mb-4">
        <p className="text-xs font-semibold text-amber-900 mb-2 flex items-center">
          <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
          You're signed in as a different account
        </p>
        <dl className="text-xs text-amber-900 space-y-1.5">
          <div className="flex gap-2">
            <dt className="w-20 text-amber-700">Invited:</dt>
            <dd className="font-medium" data-testid="invited-email">{invite?.collaborator_email}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-20 text-amber-700">You are:</dt>
            <dd className="font-medium" data-testid="current-email">{currentEmail}</dd>
          </div>
        </dl>
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={onSwitchAccount}
        data-testid="switch-account-btn"
      >
        <LogOut className="w-3.5 h-3.5 mr-1.5" />
        Sign out and switch account
      </Button>
    </Card>
  );
}

function PendingReadyToAccept({ invite, acting, actionError, onAccept, onDecline }) {
  return (
    <Card testId="state-ready-to-accept">
      <ResourceCard invite={invite} />
      {actionError && (
        <div className="bg-red-50 border border-red-100 rounded-md px-3 py-2 text-xs text-red-700 mb-3">
          {actionError}
        </div>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onDecline}
          disabled={acting}
          data-testid="decline-btn"
        >
          Decline
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={onAccept}
          disabled={acting}
          data-testid="accept-btn"
        >
          {acting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
          Accept and open
        </Button>
      </div>
    </Card>
  );
}

function AcceptedState({ invite, resourceType, resourceId, onOpenResource }) {
  const openLabel =
    resourceType === 'template' ? 'Open template' :
    resourceType === 'design'   ? 'Open design'   :
    'Open resource';
  return (
    <Card testId="state-accepted">
      <div className="text-center">
        <span className="inline-flex w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center mb-3">
          <CheckCircle2 className="w-7 h-7" />
        </span>
        <h1 className="text-lg font-semibold text-gray-900">You've joined this resource</h1>
        <p className="text-sm text-gray-500 mt-1.5 mb-5">
          {invite?.resource_name && (
            <>You now have access to <strong className="text-gray-700">{invite.resource_name}</strong>.</>
          )}
        </p>
      </div>
      <Button
        type="button"
        className="w-full"
        onClick={() => onOpenResource(resourceType, resourceId)}
        disabled={!resourceType || !resourceId}
        data-testid="open-resource-btn"
      >
        {openLabel} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
      </Button>
    </Card>
  );
}

function TerminalState({ icon: Icon, tone, title, body, extra, navigate }) {
  const palette = {
    amber:   'bg-amber-50 text-amber-600',
    gray:    'bg-gray-100 text-gray-500',
    red:     'bg-red-50 text-red-500',
  }[tone] || 'bg-gray-100 text-gray-500';
  return (
    <Card testId={`state-terminal-${tone}`}>
      <div className="text-center">
        <span className={`inline-flex w-14 h-14 rounded-full items-center justify-center mb-3 ${palette}`}>
          <Icon className="w-7 h-7" />
        </span>
        <h1 className="text-lg font-semibold text-gray-900" data-testid="terminal-title">
          {title}
        </h1>
        <p className="text-sm text-gray-500 mt-1.5" data-testid="terminal-body">
          {body}
        </p>
        {extra}
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full mt-5"
        onClick={() => navigate('/')}
        data-testid="back-home-btn"
      >
        Back to iVisora
      </Button>
    </Card>
  );
}
